import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Icon } from '../Icon';
import { useStore } from '@store/StoreProvider';
import { t } from '@store/I18nStore'

const PRESETS = ['#0059E1', '#7c3aed', '#06b6d4', '#22c55e', '#ec4899'];

export const FeaturesSection: React.FC = observer(() => {
  const [light, setLight] = useState(false);
  const [accent, setAccent] = useState('#0059E1');
  const { i18n } = useStore();
  const t = (key: string) => i18n.t(key);

  const previewStyle = light
    ? { background: '#ffffff', borderColor: 'rgba(15,23,42,.12)', boxShadow: '0 10px 30px -12px rgba(2,6,23,.5)' }
    : { background: '#0b1020', borderColor: 'rgba(255,255,255,.10)', boxShadow: 'none' };

  return (
    <section id="features" className="relative py-14 sm:py-20">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="reveal inline-flex items-center gap-2 text-xs font-semibold text-brand2 uppercase tracking-widest mb-4">
            <span className="w-8 h-px bg-brand2" /> {t('landing.features.title')} <span className="w-8 h-px bg-brand2" />
          </div>
          <h2 className="reveal text-3xl sm:text-5xl font-extrabold">{t('landing.features.one_console')} <span className="text-gradient">{t('landing.channels.title').replace(/\.$/, '')}</span></h2>
          <p className="reveal mt-4 text-slate-400 text-lg">{t('landing.features.subtitle')}</p>
        </div>

        <div data-stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-fr items-stretch">
          <article className="bento-card reveal rounded-2xl p-6 tilt h-full flex flex-col">
            <div className="ic-badge mb-4"><Icon id="ic-globe" /></div>
            <h3 className="text-lg font-bold mb-2">{t('landing.features.smart_regions')}</h3>
            <p className="text-sm text-slate-400 mb-5">{t('landing.features.geo_desc')}</p>
            <div className="space-y-2 mt-auto">
              <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm">
                <span className="px-2 py-0.5 rounded-md bg-white/10 text-[11px] font-bold tracking-wide">RU</span>
                <span className="text-slate-300">{t('widget.client.online_quoted')}</span>
              </div>
              <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-brand/10 border border-brand/30 text-sm">
                <span className="px-2 py-0.5 rounded-md bg-brand/20 text-brand2 text-[11px] font-bold tracking-wide">EN</span>
                <span className="text-brand2">«We are online»</span>
              </div>
            </div>
          </article>

          <article className="bento-card reveal rounded-2xl p-6 tilt h-full flex flex-col">
            <div className="ic-badge mb-4"><Icon id="ic-zap" /></div>
            <h3 className="text-lg font-bold mb-2">{t('landing.features.slash_phrases')}</h3>
            <p className="text-sm text-slate-400 mb-5">{t('landing.features.shortcuts_desc')}</p>
            <div className="rounded-xl bg-black/40 border border-white/10 p-3 font-mono text-sm mt-auto">
              <div className="flex items-center gap-2 text-slate-300">
                <span className="text-brand2">&gt;</span>
                <span id="typedCmd" />
                <span className="inline-block w-2 h-4 bg-brand2 align-middle animate-blink" />
              </div>
              <div id="cmdSuggest" className="mt-3 space-y-1.5 opacity-0 transition-opacity duration-300">
                <div className="px-2 py-1.5 rounded-lg bg-brand/15 border border-brand/30 text-xs">/{t('help.articles.templates.hint_demo_shortcut_hello')} · <span className="text-slate-400">{t('demo.messages.greeting')}</span></div>
                <div className="px-2 py-1.5 rounded-lg bg-white/5 text-xs">/прайс · <span className="text-slate-400">{t('demo.messages.sending_price')}</span></div>
              </div>
            </div>
          </article>

          <article className="bento-card reveal rounded-2xl p-6 tilt h-full flex flex-col">
            <div className="ic-badge mb-4"><Icon id="ic-chart" /></div>
            <h3 className="text-lg font-bold mb-2">{t('stats.hourly_load_kpi')}</h3>
            <p className="text-sm text-slate-400 mb-4">{t('stats.activity_hint')}</p>
            <svg id="loadChart" viewBox="0 0 320 120" className="w-full h-28 mt-auto">
              <defs>
                <linearGradient id="gline" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#7c3aed" /><stop offset="100%" stopColor="#0059E1" />
                </linearGradient>
                <linearGradient id="gfill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0059E1" stopOpacity=".35" /><stop offset="100%" stopColor="#0059E1" stopOpacity="0" />
                </linearGradient>
              </defs>
              <g stroke="#1e2750" strokeWidth="1">
                <line x1="0" y1="30" x2="320" y2="30" /><line x1="0" y1="60" x2="320" y2="60" /><line x1="0" y1="90" x2="320" y2="90" />
              </g>
              <path id="chartFill" d="M0,100 L0,90 L46,70 L92,20 L137,95 L183,55 L228,95 L274,45 L320,80 L320,120 L0,120 Z" fill="url(#gfill)" opacity="0" />
              <path id="chartLine" d="M0,90 L46,70 L92,20 L137,95 L183,55 L228,95 L274,45 L320,80" fill="none" stroke="url(#gline)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div className="mt-2 flex justify-between text-[10px] text-slate-500"><span>00:00</span><span>08:00</span><span>15:00</span><span>23:00</span></div>
          </article>

          <article className="bento-card reveal rounded-2xl p-6 tilt h-full flex flex-col">
            <div className="ic-badge mb-4"><Icon id="ic-palette" /></div>
            <h3 className="text-lg font-bold mb-2">{t('billing.features.full_widget_customization')}</h3>
            <p className="text-sm text-slate-400 mb-5">{t('landing.demo.themes_hint')}</p>
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setLight(false)}
                className={`theme-btn flex-1 py-2 rounded-xl border text-sm font-semibold inline-flex items-center justify-center gap-1.5 ${!light ? 'border-brand bg-brand/15' : 'border-white/10 text-slate-400'}`}
              >
                <Icon id="ic-moon" className="ic ic-sm" /> {t('widget.theme.dark')}
              </button>
              <button
                type="button"
                onClick={() => setLight(true)}
                className={`theme-btn flex-1 py-2 rounded-xl border text-sm font-semibold inline-flex items-center justify-center gap-1.5 ${light ? 'border-brand bg-brand/15' : 'border-white/10 text-slate-400'}`}
              >
                <Icon id="ic-sun" className="ic ic-sm" /> {t('widget.theme.light')}
              </button>
            </div>
            <div className="flex gap-2 mb-4">
              {PRESETS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setAccent(color)}
                  className={`w-7 h-7 rounded-full ring-2 cursor-pointer ${accent === color ? 'ring-white' : 'ring-transparent'}`}
                  style={{ background: color }}
                  aria-label={`${t('app.fields.color')} ${color}`}
                />
              ))}
            </div>
            <div id="widgetPreview" className="rounded-2xl p-3.5 border transition-colors duration-300 mt-auto" style={previewStyle}>
              <div className="flex items-start gap-2.5">
                <span className="grid place-items-center w-8 h-8 rounded-xl text-white shrink-0" style={{ background: accent }}>
                  <Icon id="ic-message" className="ic ic-sm" />
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-bold leading-snug" style={{ color: light ? '#0f172a' : '#e7ecff' }}>{t('demo.messages.hello')}</div>
                  <div className="text-[11px] mt-0.5" style={{ color: light ? '#64748b' : '#8aa0c8' }}>{t('widget.client.ready_questions')}</div>
                </div>
              </div>
              <button type="button" className="mt-3 w-full py-2 rounded-xl text-xs font-semibold text-white transition-colors" style={{ background: accent }}>{t('widget.client.online')}</button>
            </div>
          </article>

          <article className="bento-card reveal rounded-2xl p-6 tilt h-full flex flex-col">
            <div className="ic-badge mb-4"><Icon id="ic-link" /></div>
            <h3 className="text-lg font-bold mb-2">{t('landing.features.site_and_telegram_one_console')}</h3>
            <p className="text-sm text-slate-400 mb-5">{t('landing.two_channels_one_console')}</p>
            <div className="mt-auto flex items-stretch gap-2.5">
              <div className="flex-1 grid place-items-center py-4 rounded-2xl bg-white/5 border border-white/10">
                <Icon id="ic-monitor" className="ic text-slate-300" />
                <div className="text-[11px] mt-2 text-slate-300">{t('channels.types.widget_on_site')}</div>
              </div>
              <div className="self-center text-brand2 text-xl shrink-0">+</div>
              <div className="flex-1 grid place-items-center py-4 rounded-2xl bg-brand/10 border border-brand/30">
                <Icon id="ic-send" className="ic text-brand2" />
                <div className="text-[11px] mt-2 text-brand2">{t('channels.types.telegram_bot')}</div>
              </div>
            </div>
          </article>

          <article className="bento-card reveal rounded-2xl p-6 tilt h-full flex flex-col">
            <div className="ic-badge mb-4"><Icon id="ic-user-plus" /></div>
            <h3 className="text-lg font-bold mb-2">{t('landing.features.instant_invites')}</h3>
            <p className="text-sm text-slate-400 mb-5">{t('landing.features.team_desc')}</p>
            <div className="flex -space-x-2 mb-4">
              <span className="w-9 h-9 rounded-full bg-gradient-to-br from-brand to-indigo-500 ring-2 ring-deep grid place-items-center text-xs font-bold">A</span>
              <span className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 ring-2 ring-deep grid place-items-center text-xs font-bold">M</span>
              <span className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 ring-2 ring-deep grid place-items-center text-xs font-bold">K</span>
              <span className="w-9 h-9 rounded-full bg-white/10 ring-2 ring-deep grid place-items-center text-xs">+5</span>
            </div>
            <div className="flex gap-2 mt-auto">
              <div className="flex-1 h-9 rounded-lg bg-white/5 border border-white/10 px-3 flex items-center text-xs text-slate-500">team@company…</div>
              <button type="button" className="px-3 h-9 rounded-lg bg-brand text-xs font-semibold">{t('team.invite.button')}</button>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
});
